import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowContainer, Popover } from 'react-tiny-popover';
import envelopeSvg from 'assets/icons/envelope.svg';
import Auth from 'modules/Auth';
import Button, { Color, Size } from 'lib/Button';
import { VALID_EMAIL_REGEX } from 'consts/email';
import { CircularLoadingSpinner, SpinnerSize } from 'lib/CircularLoadingSpinner/CircularLoadingSpinner';
import { addToastNotification } from 'store/notification/reducer';
import { getEnvironmentId } from 'store/environment/selectors';
import { getTimezone } from 'store/publisher/selectors';
import type { Period } from './NotificationPanel/useEmailNotificationApi';
import useEmailNotificationApi from './NotificationPanel/useEmailNotificationApi';
import NotificationPanel from './NotificationPanel';

const INVALID_EMAIL_MESSAGE = 'The email address entered is invalid. Please use another email address and try again.';

const EmailNotificationButton = (): React.ReactElement => {
  const dispatch = useDispatch();
  const { email: userEmail } = Auth.getData();
  const defaultEmail = userEmail || '';
  const marketTimezone = useSelector(getTimezone);
  const environmentId = useSelector(getEnvironmentId);

  const { isLoading, isSaving, isEnabled, savedEmail, savedPeriod, handleSave, handleUnsubscribe } =
    useEmailNotificationApi(environmentId, defaultEmail, dispatch);

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [draftEmail, setDraftEmail] = useState(defaultEmail);
  const [draftPeriod, setDraftPeriod] = useState<Period>('Last 24 hours');

  const handleClosePanel = useCallback(() => setIsPanelOpen(false), []);

  const handleToggleClick = (): void => {
    if (!isPanelOpen) {
      setDraftEmail(savedEmail);
      setDraftPeriod(savedPeriod);
    }
    setIsPanelOpen((prev) => !prev);
  };

  const onSave = useCallback(async (): Promise<void> => {
    if (!VALID_EMAIL_REGEX.test(draftEmail.trim())) {
      dispatch(addToastNotification({ type: 'Failure', description: INVALID_EMAIL_MESSAGE, timeout: 2000 }));
      return;
    }
    try {
      await handleSave(draftEmail.trim(), draftPeriod, isEnabled);
      setIsPanelOpen(false);
    } catch {} // eslint-disable-line no-empty
  }, [handleSave, draftEmail, draftPeriod, isEnabled, dispatch]);

  const onUnsubscribe = useCallback(async (): Promise<void> => {
    await handleUnsubscribe();
    setIsPanelOpen(false);
  }, [handleUnsubscribe]);

  const toggleLabel = isEnabled ? 'Subscribed' : 'Notify me';

  return (
    <div className="flex items-center gap-2">
      {isLoading && (
        <div className="w-5 h-5" data-test-id="email-notification-loading">
          <CircularLoadingSpinner size={SpinnerSize.SMALL} />
        </div>
      )}
      <Popover
        containerClassName="z-50 py-1"
        positions={['bottom', 'top']}
        align="end"
        reposition
        isOpen={isPanelOpen}
        onClickOutside={handleClosePanel}
        content={({ position, childRect, popoverRect }) => (
          <ArrowContainer
            position={position}
            childRect={childRect}
            popoverRect={popoverRect}
            arrowColor="var(--transparent-color)"
            arrowSize={0}
          >
            <NotificationPanel
              isEnabled={isEnabled}
              isSaving={isSaving}
              draftEmail={draftEmail}
              draftPeriod={draftPeriod}
              savedEmail={savedEmail}
              savedPeriod={savedPeriod}
              marketTimezone={marketTimezone}
              onEmailChange={setDraftEmail}
              onPeriodChange={setDraftPeriod}
              onSave={onSave}
              onCancel={handleClosePanel}
              onUnsubscribe={onUnsubscribe}
            />
          </ArrowContainer>
        )}
      >
        <div>
          <Button
            dataTestId="email-notification-toggle"
            svg={envelopeSvg}
            label={toggleLabel}
            color={isEnabled ? Color.PRIMARY : Color.TERTIARY}
            size={Size.LARGE}
            isDisabled={isLoading}
            onClick={handleToggleClick}
          />
        </div>
      </Popover>
    </div>
  );
};

export default EmailNotificationButton;
